/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Ad, AdBreak, AdContentTimePosition, AdPlaybackPolicyHandler, Promo } from '@quickplay/player-interface';
import { TIME_THRESHOLD } from './PromoPlayerImpl';

/**
 * Builds an Ad that represents each one of the provided Promos and group them
 * into an Ad Break.
 * @param promos a list of Promos
 * @param duration the total duration of the content including Promos
 */
export function prepareAdBreak(promos: Promo[], duration: any): any {
  let adBreaks: AdBreak[] | undefined = undefined;
  let ads: Ad[] | undefined = undefined;

  if (promos.length) {
    adBreaks = [];
    ads = [];
  }

  let adBreak: AdBreak | undefined;
  let nextAdBreakStart = 0;
  let adSequence = 0;

  promos.forEach((promo: Promo, index: number) => {
    if (nextAdBreakStart === index) {
      adBreak = undefined;
      adSequence = 0;

      const { nextAdBreakStart_, adBreak_ } = evaluatePromosInRange(
        nextAdBreakStart,
        promos,
        adBreak,
        promo,
        adBreaks,
        duration,
      );

      nextAdBreakStart = nextAdBreakStart_;
      adBreak = adBreak_;
    }

    ads?.push(toAd(promo, adSequence++, adBreak));
  });

  // return the ad breaks and the ads
  return {
    adBreaks_: adBreaks,
    ads_: ads,
  };
}

/**
 * Builds the Ad Break corresponding to a list of promos.
 * @param nextAdBreakStart
 * @param promos the list of promos
 * @param adBreak the existing ad break
 * @param promo the promo to evaluate
 * @param adBreaks the list of ad breaks
 * @param duration the total duration of the content including Promos
 */
function evaluatePromosInRange(
  nextAdBreakStart: number,
  promos: Promo[],
  adBreak: AdBreak | undefined,
  promo: Promo,
  adBreaks: AdBreak[] | undefined,
  duration: any,
): any {
  for (let promoIndexForAdBreak = nextAdBreakStart; promoIndexForAdBreak < promos.length; promoIndexForAdBreak++) {
    const promoForAdBreak: Promo = promos[promoIndexForAdBreak];

    if (!adBreak) {
      adBreak = toAdBreak(promoForAdBreak, promo);
    } else if (adBreak.startTime + adBreak.duration === promoForAdBreak.start) {
      adBreak.duration += promoForAdBreak.duration;
      adBreak.totalAds++;
    } else {
      nextAdBreakStart = promoIndexForAdBreak;

      if (adBreak) {
        adBreaks?.push(adBreak);
      }

      return {
        nextAdBreakStart_: nextAdBreakStart,
        adBreak_: adBreak,
      };
    }

    if (isPostroll(duration, adBreak)) {
      adBreak.position = AdContentTimePosition.POSTROLL;
    }

    if (promoIndexForAdBreak === promos.length - 1) {
      if (adBreak) {
        adBreaks?.push(adBreak);
      }

      break;
    }
  }

  return {
    nextAdBreakStart_: nextAdBreakStart,
    adBreak_: adBreak,
  };
}

/**
 * Returns a boolean value indicating whether the provided ad break is postroll
 * or not.
 * @param duration the total duration of the content including Promos
 * @param adBreak the ad break used to determine whether it is postroll or not
 */
function isPostroll(duration: number, adBreak: AdBreak): boolean {
  return !isNaN(duration) && adBreak.startTime + adBreak.duration >= duration;
}

/**
 * Builds an Ad Break with the provided data.
 * @param promoForAdBreak the promo which contains the data for the new Ad Break
 * @param promo he promo which contains the data for the new Ad Break
 */
function toAdBreak(promoForAdBreak: Promo, promo: Promo): AdBreak {
  return {
    position: promoForAdBreak.start === 0 ? AdContentTimePosition.PREROLL : AdContentTimePosition.MIDROLL,
    startTime: promoForAdBreak.start,
    duration: promo.duration,
    totalAds: 1,
    active: true,
    isSkippable: !!promo.skippable,
  };
}

/**
 * Builds an Ad with the provided data.
 * @param promo the promo which contains the data for the new Ad
 * @param adSequence the sequence number that corresponds to the new Ad
 * @param adBreak the Ad Break the new Ad belongs to
 */
function toAd(promo: Promo, adSequence: number, adBreak: AdBreak | undefined): Ad {
  return {
    adId: promo.contentId,
    sequence: adSequence + 1,
    start: promo.start,
    duration: promo.duration,
    title: '',
    isSkippable: promo.skippable ?? false,
    adBreak: adBreak,
    isAutoPlay: true,
    skipOffset: promo.skipOffset,
  };
}

/**
 * Determines whether the provided playhead position is within an ad break or not.
 * @param playhead the playhead position in seconds
 * @param adBreaks the list of ad breaks
 */
export function isInAdBreak(playhead: number, adBreaks: AdBreak[] | undefined): AdBreak | null {
  if (!adBreaks) {
    return null;
  }

  for (const adBreak of adBreaks) {
    if (compare(adBreak.startTime, playhead) && playhead < adBreak.startTime + adBreak.duration) {
      return adBreak;
    }
  }

  return null;
}

/**
 * Determines whether the provided playhead position is within an ad or not.
 * @param playhead the playhead position in seconds
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function isInAdvert(playhead: number, adBreaks: AdBreak[] | undefined, ads: Ad[] | undefined): Ad | null {
  if (!adBreaks || !ads) {
    return null;
  }

  for (const adBreak of adBreaks) {
    if (compare(adBreak.startTime, playhead) && playhead < adBreak.startTime + adBreak.duration) {
      const filteredAds: Ad[] = ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime);

      let adStart = adBreak.startTime;

      for (let i = 0; i < filteredAds.length; i++) {
        if (playhead < adStart + filteredAds[i].duration) {
          return filteredAds[i];
        }

        adStart += filteredAds[i].duration;
      }

      return null;
    }
  }

  return null;
}

/**
 * Returns the current Ad duration if any.
 * @param playhead the playhead position
 * @param adBreak the ad break
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdDuration(
  playhead: number,
  adBreak: AdBreak,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): number | null {
  if (!adBreaks) {
    return null;
  }

  const filteredAds: Ad[] = ads ? ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime) : [];

  let adStart = adBreak.startTime;

  for (let i = 0; i < filteredAds.length; i++) {
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return filteredAds[i].duration;
    }

    adStart += filteredAds[i].duration;
  }

  return null;
}

/**
 * Returns the total duration of the completed ad breaks.
 * @param playhead the playhead position
 * @param adBreak the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdBreakCompletedAdsDuration(playhead: number, adBreak: AdBreak, ads: Ad[] | undefined): number {
  if (!ads) {
    return 0;
  }

  const filteredAds: Ad[] = ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime);

  let adStart = adBreak.startTime;

  let completedAdsDuration = 0;

  for (let i = 0; i < filteredAds.length; i++) {
    // if adStart less than or equal to playhead
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return completedAdsDuration;
    }

    adStart += filteredAds[i].duration;
    completedAdsDuration += filteredAds[i].duration;
  }

  return completedAdsDuration;
}

/**
 * Returns the ad end time corresponding to the current ad.
 * @param currentAd the current ad
 * @param ads the list of ads
 */
export function getCurrentAdEndTime(currentAd: Ad, ads: Ad[] | undefined): number {
  const completedCurrentAdBreakAdsDuration = ads
    ?.filter((ad: Ad) => ad.adBreak?.startTime === currentAd.adBreak?.startTime && ad.sequence < currentAd.sequence)
    .map((filteredAd: Ad) => filteredAd.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);

  if (completedCurrentAdBreakAdsDuration && currentAd.adBreak) {
    return currentAd.adBreak.startTime + completedCurrentAdBreakAdsDuration + currentAd.duration;
  }

  return currentAd.duration;
}

/**
 * Given two timestamps this function will return a boolean value indicating whether the first
 * timestamp is less than or equal to the second timestamp or not.
 *
 * Two timestamps are considered equal when the absolute value of the difference between the two
 * timestamps is less than TIME_THRESHOLD.
 *
 * @param first the first timestamp
 * @param second the second timestamp
 */
function compare(first: number, second: number): boolean {
  if (first < second) {
    return true;
  }

  return Math.abs(first - second) < TIME_THRESHOLD;
}

/**
 * Returns a boolean value indicating whethere there are promos available or not.
 * @param adBreaks the list of ad breaks
 */
export function isPromosAvailable(adBreaks: AdBreak[] | undefined): boolean {
  return !!(adBreaks && adBreaks.length);
}

/**
 * Determines whether there has been an Ad start event by comparing the player status
 * in two different playhead positions.
 * @param currentPlayhead the current playhead position
 * @param previousPlayhead the previous playhead position
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function isAdStartEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): Ad | null {
  const currentAd = isInAdvert(currentPlayhead, adBreaks, ads);

  if (!currentAd) {
    return null;
  }

  const previousAd = isInAdvert(previousPlayhead, adBreaks, ads);

  if (previousAd) {
    if (currentAd.adId !== previousAd.adId) {
      //this.currentAd_ = currentAd;
      return currentAd;
    }
  } else {
    //this.currentAd_ = currentAd;
    return currentAd;
  }

  return null;
}

/**
 * Determines whether there has been an Ad Break start event by comparing the player
 * status in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 */
export function isAdBreakStartEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
): AdBreak | null {
  const adBreak = isInAdBreak(currentPlayhead, adBreaks);

  if (adBreak && !isInAdBreak(previousPlayhead, adBreaks)) {
    //this.currentAdBreak_ = adBreak;
    return adBreak;
  }

  return null;
}

/**
 * Determines whether there has been an Ad end event by comparing the player status
 * in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 * @param ads
 */
export function isAdEndEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): Ad | null {
  const previousAd = isInAdvert(previousPlayhead, adBreaks, ads);
  const currentAd = isInAdvert(currentPlayhead, adBreaks, ads);

  if (previousAd) {
    if (currentAd) {
      if (previousAd.adId !== currentAd.adId) {
        //this.currentAd_ = undefined;
        return previousAd;
      }
    } else {
      //this.currentAd_ = undefined;
      return previousAd;
    }
  }

  return null;
}

/**
 * Determines whether there has been an Ad Break end event by comparing the player
 * status in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 * @param currentTime
 * @param adPlaybackPolicyHandler
 */
export function isAdBreakEndEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  currentTime: number,
  adPlaybackPolicyHandler: AdPlaybackPolicyHandler,
): AdBreak | null {
  const adBreak = isInAdBreak(previousPlayhead, adBreaks);

  if (!isInAdBreak(currentPlayhead, adBreaks) && adBreak) {
    //this.currentAdBreak_ = undefined;
    adPlaybackPolicyHandler.setWatchedAdBreak(adBreak);
    adPlaybackPolicyHandler.resolveSeekIntent(currentTime);

    return adBreak;
  }

  return null;
}

/**
 * Returns the total duration of all provided ad breaks.
 * @param adBreaks the list of ad breaks
 */
export function getTotalAdvertsBreaksDuration(adBreaks: AdBreak[]): number {
  return adBreaks
    .map((adBreak: AdBreak) => adBreak.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue);
}

/**
 * Returns the total duration of all provided ad breaks of which start time
 * is before the current time position.
 * @param currentTime the current time position.
 * @param adBreaks the list of ad breaks
 */
export function getAdvertBreaksDurationPriorTo(currentTime: number, adBreaks: AdBreak[]): number {
  return adBreaks
    .map((adBreak: AdBreak) => (adBreak.startTime < currentTime ? adBreak.duration : 0))
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue);
}

/**
 * Returns the absolute position to seek for given a relative position. To calculate the absolute
 * position it calculates the total duration of all preroll ad breaks and all other breaks of which
 * start time is before the current time position.
 * @param playhead the relative time position.
 * @param adBreaks the list of ad breaks
 */
export function getSeekPositionFor(playhead: number, adBreaks: AdBreak[]): number {
  // prevent seek to negative playhead positions
  if (playhead < 0) {
    playhead = 0;
  }
  const adBreakNextToSeekPosition = adBreaks.filter((adBreak: AdBreak) => adBreak.startTime >= playhead).shift();

  playhead += adBreaks
    .filter((adBreak: AdBreak) => adBreak.position === AdContentTimePosition.PREROLL || adBreak.startTime < playhead)
    .map((adBreak: AdBreak) => adBreak.duration)
    .reduce((totalAdBreaksDuration: number, adBreakDuration: number) => totalAdBreaksDuration + adBreakDuration, 0);

  if (
    adBreakNextToSeekPosition &&
    playhead >= adBreakNextToSeekPosition.startTime &&
    playhead <= adBreakNextToSeekPosition.startTime + adBreakNextToSeekPosition.duration
  ) {
    playhead += adBreakNextToSeekPosition.duration;
  }
  return playhead;
}

/**
 * Returns a list of all active ad breaks of which start time is between the given range.
 * @param startPosition the start position.
 * @param endPosition the end position.
 * @param adBreaks the list of ad breaks
 */
export function getAdBreaksBetween(startPosition: number, endPosition: number, adBreaks: AdBreak[]): AdBreak[] {
  return adBreaks.filter(
    (adBreak: AdBreak) =>
      adBreak.active && adBreak.startTime >= startPosition && adBreak.startTime <= endPosition && adBreak.duration > 0,
  );
}
